import React from 'react';

import { Badge } from '@/components/ui/badge';
import { CoverageStatus } from '@/services/types/coverage-types';

interface CoverageStatusBadgeProps {
  status: CoverageStatus | null;
  showLabel?: boolean;
  className?: string;
}

export const CoverageStatusBadge: React.FC<CoverageStatusBadgeProps> = ({
  status,
  showLabel = true,
  className = '',
}) => {
  // Treat null as NEEDS_APPROVAL
  const effectiveStatus = status || CoverageStatus.NEEDS_APPROVAL;

  const statusConfig: Record<
    CoverageStatus,
    { color: string; label: string; indicator: string }
  > = {
    [CoverageStatus.NEEDS_APPROVAL]: {
      color: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-50 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-950',
      label: 'Needs Approval',
      indicator: '○',
    },
    [CoverageStatus.APPROVED]: {
      color: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-50 dark:bg-green-950 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-950',
      label: 'Approved',
      indicator: '●',
    },
    [CoverageStatus.REJECTED]: {
      color: 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-900',
      label: 'Rejected',
      indicator: '●',
    },
    [CoverageStatus.NOT_APPLICABLE]: {
      color: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-900',
      label: 'Not Applicable',
      indicator: '—',
    },
    [CoverageStatus.DUPLICATE]: {
      color: 'bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-50 dark:bg-violet-950 dark:text-violet-400 dark:border-violet-800 dark:hover:bg-violet-950',
      label: 'Duplicate',
      indicator: '◈',
    },
  };

  const config = statusConfig[effectiveStatus];

  return (
    <Badge
      variant="outline"
      className={`${config.color} ${className} ${!showLabel ? 'min-w-5 justify-center px-1.5 py-0.5' : 'px-2 py-0.5'} text-xs font-medium`}
    >
      <span className={showLabel ? 'mr-1' : ''} aria-hidden="true">
        {config.indicator}
      </span>
      {showLabel && config.label}
    </Badge>
  );
};
